import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  InputGroup,
} from "react-bootstrap";
import {
  GET_BASIC_SETTING,
  UPDATE_BASIC_SETTING,
} from "../../../../services/ApiCalls";
import { toast } from "react-toastify";
import Loader from "../../../../components/Common/Loader";
import { useTranslation } from "react-i18next";

function BasicSettings() {
  const { t } = useTranslation();

  const [configData, setConfigData] = useState({
    countries: [
      { code: "US", name: "United States", currency: "USD", language: "en" },
      { code: "VN", name: "Vietnam", currency: "VND", language: "vi" },
      { code: "TH", name: "Thailand", currency: "THB", language: "th" },
      { code: "EU", name: "European Union", currency: "EUR", language: "en" },
      { code: "GB", name: "United Kingdom", currency: "GBP", language: "en" },
    ],
    currencies: [
      { code: "USD", name: "$" },
      { code: "VND", name: "₫" },
      { code: "THB", name: "฿" },
      { code: "EUR", name: "€" },
      { code: "GBP", name: "£" },
    ],
    languages: [
      { code: "en", name: "English" },
      { code: "vi", name: "Vietnamese" },
      { code: "th", name: "Thai" },
    ],
    timezones: [
      { code: "Asia/Ho_Chi_Minh", name: "(GMT+07:00) Vietnam (Ho Chi Minh)" },
      { code: "Asia/Bangkok", name: "(GMT+07:00) Thailand (Bangkok)" },
      { code: "Asia/Kolkata", name: "(GMT+05:30) India Standard Time" },
      { code: "America/New_York", name: "(GMT-05:00) New York" },
      { code: "Europe/London", name: "(GMT+00:00) London" },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    country: "",
    address: "",
    timezone: "",
    currency: { code: "", sign: "" },
    language: { code: "", name: "" },
    earningCommision: { status: true, type: "fix", value: 0 },
    paymentMode: "",
    referralSetting: { enableReferral: "", signupBonus: "", referralBonus: "" },
  });

  useEffect(() => {
    getBasicSetting();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target || e;
    const keys = name.split(".");
    setFormData((prevData) => {
      let updatedData = { ...prevData };
      let temp = updatedData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!temp[keys[i]]) temp[keys[i]] = {};
        temp = temp[keys[i]];
      }

      if (value === "true") temp[keys[keys.length - 1]] = true;
      else if (value === "false") temp[keys[keys.length - 1]] = false;
      else temp[keys[keys.length - 1]] = value;

      return { ...updatedData };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      App_Settings: {
        Admin_Country: formData.country,
        Address: formData.address,
        Admin_TimeZone: formData.timezone,
        Admin_Currency: formData.currency.code,
        Admin_Currency_Code: formData.currency.sign,
        Payment_Mode: formData.paymentMode,
        serviceFee: Number(formData.earningCommision.value),
        taxes: 0,
        longStayDiscount: 0,
        isSocialLogin: true,
        Admin_Phone_Number: "4108289477",
        Contact_Email: "info@rentalPlatform.com",
      },
      referralSetting: {
        isActive: Boolean(formData.referralSetting.enableReferral),
        signUp: {
          amount: Number(formData.referralSetting.signupBonus),
          status: Boolean(formData.referralSetting.enableReferral),
        },
        firstOrder: {
          amount: Number(formData.referralSetting.referralBonus),
          status: Boolean(formData.referralSetting.enableReferral),
        },
      },
      _id: formData._id,
    };

    try {
      const res = await UPDATE_BASIC_SETTING(payload);
      if (res?.data?.status === "success") {
        toast.success(res.data.message);
        getBasicSetting();
      } else {
        toast.error(res?.data?.message || t("basicSettings.saveError"));
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(t("basicSettings.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const getBasicSetting = async () => {
    setLoading(true);
    try {
      const res = await GET_BASIC_SETTING();
      if (res?.data?.status === "success") {
        const apiData = res.data.data[0] || {};
        const appSettings = apiData.App_Settings || {};
        const referral = apiData.referralSetting || {};

        setFormData({
          _id: apiData?._id,
          country: appSettings.Admin_Country || "",
          address: appSettings.Address || "",
          timezone: appSettings.Admin_TimeZone || "",
          currency: {
            code: appSettings.Admin_Currency || "",
            sign: appSettings.Admin_Currency_Code || "",
          },
          language: { code: "en", name: "English" },
          earningCommision: {
            status: true,
            type: "fix",
            value: appSettings.serviceFee || 0,
          },
          paymentMode: appSettings.Payment_Mode || "sandbox",
          referralSetting: {
            enableReferral: referral.isActive || false,
            signupBonus: referral.signUp?.amount || "",
            referralBonus: referral.firstOrder?.amount || "",
          },
        });
      } else {
        toast.error(t("basicSettings.fetchError"));
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(t("basicSettings.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="basic-info-wrapper">
      {loading && <Loader />}
      <Container className="my-5">
        <h4>{t("basicSettings.title")}</h4>
        <p className="text-muted">{t("basicSettings.description")}</p>

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group as={Row}>
                <Form.Label column sm={4}>
                  {t("basicSettings.country")}
                </Form.Label>
                <Col sm={8}>
                  <Form.Select
                    value={formData.country}
                    name="country"
                    onChange={handleChange}
                  >
                    <option value="">{t("basicSettings.selectOption")}</option>
                    {configData?.countries?.map((item) => (
                      <option key={item.code} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group as={Row}>
                <Form.Label column sm={4}>
                  {t("basicSettings.address")}
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t("basicSettings.enterAddress")}
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group as={Row}>
                <Form.Label column sm={4}>
                  {t("basicSettings.timezone")}
                </Form.Label>
                <Col sm={8}>
                  <Form.Select
                    value={formData.timezone}
                    name="timezone"
                    onChange={handleChange}
                  >
                    <option value="">{t("basicSettings.selectOption")}</option>
                    {configData?.timezones?.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group as={Row}>
                <Form.Label column sm={4}>
                  {t("basicSettings.currency")}
                </Form.Label>
                <Col sm={8}>
                  <Form.Select
                    value={formData.currency.code}
                    name="currency.code"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        currency: {
                          code: e.target.value,
                          sign:
                            configData?.currencies?.find(
                              (c) => c.code === e.target.value
                            )?.name || "",
                        },
                      })
                    }
                  >
                    <option value="">{t("basicSettings.selectOption")}</option>
                    {configData?.currencies?.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.code} ({item.name})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group as={Row}>
                <Form.Label column sm={4}>
                  {t("basicSettings.paymentMode")}
                </Form.Label>
                <Col sm={8}>
                  <Form.Select
                    value={formData.paymentMode}
                    name="paymentMode"
                    onChange={handleChange}
                  >
                    <option value="">{t("basicSettings.selectOption")}</option>
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <div className="commission-card-wrapper p-3 rounded-2 mb-5">
            <h5 className="my-3">{t("basicSettings.commission.title")}</h5>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="d-flex gap-3 align-items-center">
                  <Form.Label>
                    {t("basicSettings.commission.status")}
                  </Form.Label>
                  <ToggleButtonGroup
                    type="radio"
                    name="earningCommision.status"
                    value={formData.earningCommision.status}
                    onChange={(value) =>
                      handleChange({
                        target: { name: "earningCommision.status", value },
                      })
                    }
                  >
                    <ToggleButton id="statustrue" value={true}>
                      {t("basicSettings.commission.true")}
                    </ToggleButton>
                    <ToggleButton id="statusfalse" value={false}>
                      {t("basicSettings.commission.false")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="d-flex gap-3 align-items-center">
                  <Form.Label>{t("basicSettings.commission.type")}</Form.Label>
                  <ToggleButtonGroup
                    type="radio"
                    name="earningCommision.type"
                    value={formData.earningCommision.type}
                    onChange={(value) =>
                      handleChange({
                        target: { name: "earningCommision.type", value },
                      })
                    }
                  >
                    <ToggleButton id="fix" value="fix">
                      {t("basicSettings.commission.fix")}
                    </ToggleButton>
                    <ToggleButton id="percentage" value="percentage">
                      {t("basicSettings.commission.percentage")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t("basicSettings.commission.amount")}</Form.Label>
              <Col sm={3}>
                <InputGroup>
                  {formData.earningCommision.type === "fix" && (
                    <InputGroup.Text>{formData.currency.sign}</InputGroup.Text>
                  )}
                  <Form.Control
                    type="number"
                    name="earningCommision.value"
                    value={formData.earningCommision.value}
                    onChange={handleChange}
                    min="0"
                    step={
                      formData.earningCommision.type === "percentage"
                        ? "0.1"
                        : "1"
                    }
                  />
                  {formData.earningCommision.type === "percentage" && (
                    <InputGroup.Text>%</InputGroup.Text>
                  )}
                </InputGroup>
              </Col>
            </Form.Group>
          </div>

          
          <div className="referral-card-wrapper p-3 rounded-2 mb-5">
            <h5 className="my-3">{t("basicSettings.referral.title")}</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 d-flex gap-3 align-items-center">
                  <Form.Label>
                    {t("basicSettings.referral.enableReferral")}
                  </Form.Label>
                  <ToggleButtonGroup
                    type="radio"
                    name="referralSetting.enableReferral"
                    value={formData.referralSetting.enableReferral}
                    onChange={(value) =>
                      handleChange({
                        target: {
                          name: "referralSetting.enableReferral",
                          value,
                        },
                      })
                    }
                  >
                    <ToggleButton id="referral-yes" value={true}>
                      {t("basicSettings.referral.yes")}
                    </ToggleButton>
                    <ToggleButton id="referral-no" value={false}>
                      {t("basicSettings.referral.no")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("basicSettings.referral.signupBonus")}
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>{formData.currency.sign}</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="referralSetting.signupBonus"
                      value={formData.referralSetting.signupBonus}
                      onChange={handleChange}
                      min="0"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("basicSettings.referral.referralBonus")}
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>{formData.currency.sign}</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="referralSetting.referralBonus"
                      value={formData.referralSetting.referralBonus}
                      onChange={handleChange}
                      min="0"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="text-center">
            <Button variant="primary" type="submit" disabled={loading}>
              {loading
                ? t("basicSettings.saving")
                : t("basicSettings.saveButton")}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
}

export default BasicSettings;
